import { addDays } from 'date-fns';
import { useMemo } from 'react';
import { CalendarEventOrganiser } from '~/components/PlannerView/CalendarEventOrganiser';
import type { CalendarEvent } from '~/components/PlannerView/PlannerView';
import type { ZoomLevel } from '~/routes/calendar._index';
import { buildCalendarWeekEvents } from '../buildCalendarWeekEvents';
import type { CalendarEventWithClip, PlaceData } from '../types';

/**
 * Pre-computed event positions for a range of weeks
 * This is the KEY performance optimization - we compute positions ONCE
 * for all visible weeks instead of per-week
 */
export type WeekEventPositions = {
  /** Event vertical positions for non-booking events */
  eventVpos: Record<string, number>;
  /** Event vertical positions for booking events */
  bookingVpos: Record<string, number>;
  /** Number of booking lanes needed */
  bookingLaneCount: number;
  /** All events for this week with clip metadata */
  weekEvents: CalendarEventWithClip[];
};

type UseCalendarEventPositionsParams = {
  /** Calendar jobs for the site */
  calendar: PlaceData['calendar'] | undefined;
  /** Site ID */
  siteId: string | undefined;
  /** Array of week start dates to compute positions for */
  visibleWeekStarts: Date[];
  /** Whether bookings are hidden */
  bookingsHidden: boolean;
};

/**
 * Hook that pre-computes event positions for all visible weeks
 *
 * PERFORMANCE: This lifts the CalendarEventOrganiser computation from
 * VirtualizedWeek (called per week) to the parent level (called once).
 *
 * Instead of creating 2 CalendarEventOrganiser instances per visible week,
 * we create them once and cache the results by week.
 */
export const useCalendarEventPositions = ({
  calendar,
  siteId,
  visibleWeekStarts,
  bookingsHidden,
}: UseCalendarEventPositionsParams): Map<number, WeekEventPositions> => {
  // Position hash keeps only fields that affect layout; display hash tracks label fields.
  const calendarPositionHash = useMemo(() => {
    if (!calendar || calendar.length === 0) return '';
    return calendar
      .map(
        (job) =>
          `${job.id}:${job.status}:${job.start}:${job.end ?? ''}:${
            job.filtered ?? ''
          }`
      )
      .join('|');
  }, [calendar]);

  const calendarDisplayHash = useMemo(() => {
    if (!calendar || calendar.length === 0) return '';
    return calendar
      .map((job) =>
        [
          job.id,
          job.summary ?? '',
          job.assignee ?? '',
          JSON.stringify(job.attributes ?? {}),
          JSON.stringify(job.tags ?? []),
          job.scheduledStartTime ?? '',
          job.scheduledEndTime ?? '',
          job.timeZone ?? '',
          job.filtered ?? '',
        ].join(':')
      )
      .join('|');
  }, [calendar]);

  // Pre-compute all event positions for visible weeks using the minimal position hash
  const baseWeekPositions = useMemo(() => {
    const result = new Map<number, WeekEventPositions>();

    if (!calendar || !siteId || visibleWeekStarts.length === 0) {
      return result;
    }

    // Process each visible week
    for (const weekStart of visibleWeekStarts) {
      const weekKey = weekStart.getTime();
      const weekEvents = buildCalendarWeekEvents(
        calendar,
        siteId,
        weekStart,
        bookingsHidden
      );

      // Calculate vpos for non-booking events
      const nonBookingEvents = weekEvents.filter(
        (event) => event.event.status !== 'Booking'
      );

      const organiser = new CalendarEventOrganiser({
        events: nonBookingEvents as unknown as CalendarEvent[],
        zoomLevel: 'month' as ZoomLevel,
      });
      const eventVpos = organiser.getEventVpos();

      // Calculate vpos for booking events separately
      // Note: CalendarEventOrganiser ignores 'Booking' status, so we
      // temporarily use a different status for lane calculation
      const bookings = weekEvents
        .filter((event) => event.event.status === 'Booking')
        .map((event) => {
          const origStart = new Date(event.event.start);
          const origEnd = event.event.end
            ? new Date(event.event.end)
            : origStart;
          // Half-open interval for lane calculation
          const adjustedEnd =
            origEnd > origStart ? addDays(origEnd, -1) : origStart;
          return {
            ...event,
            event: {
              ...event.event,
              status: 'Created', // Temporary status for organiser
              start: origStart.toISOString(),
              end: adjustedEnd.toISOString(),
            },
          } as CalendarEvent;
        });

      let bookingVpos: Record<string, number> = {};
      let bookingLaneCount = 0;

      if (bookings.length > 0) {
        const bookingOrganiser = new CalendarEventOrganiser({
          events: bookings,
          zoomLevel: 'month' as ZoomLevel,
        });
        bookingVpos = bookingOrganiser.getEventVpos();
        const vals = Object.values(bookingVpos);
        bookingLaneCount = vals.length > 0 ? Math.max(...vals) + 1 : 0;
      }

      result.set(weekKey, {
        eventVpos,
        bookingVpos,
        bookingLaneCount,
        weekEvents,
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarPositionHash, siteId, visibleWeekStarts, bookingsHidden]);

  // Hydrate display-only fields without re-running the expensive positional computation
  const weekPositionsMap = useMemo(() => {
    if (baseWeekPositions.size === 0) return baseWeekPositions;

    const calendarById = new Map((calendar ?? []).map((job) => [job.id, job]));
    if (!calendarDisplayHash && calendarById.size === 0)
      return baseWeekPositions;
    const hydratedPositions = new Map<number, WeekEventPositions>();

    baseWeekPositions.forEach((positions, weekKey) => {
      const updatedWeekEvents = positions.weekEvents.map((weekEvent) => {
        const job = calendarById.get(weekEvent.event.id);
        if (!job) return weekEvent;

        return {
          ...weekEvent,
          event: {
            ...weekEvent.event,
            summary: job.summary,
            assignee: job.assignee,
            attributes: job.attributes,
            tags: job.tags ?? weekEvent.event.tags,
            scheduledStartTime: job.scheduledStartTime,
            scheduledEndTime: job.scheduledEndTime,
            timeZone: job.timeZone,
            filtered: job.filtered ?? weekEvent.event.filtered,
          },
        };
      });

      hydratedPositions.set(weekKey, {
        ...positions,
        weekEvents: updatedWeekEvents,
      });
    });

    return hydratedPositions;
  }, [baseWeekPositions, calendarDisplayHash, calendar]);

  return weekPositionsMap;
};

export { getVisibleWeekStarts } from '../buildCalendarWeekEvents';
